import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Divider,
  Empty,
  Modal,
  Progress,
  Space,
  Tag,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, TeamOutlined, UploadOutlined } from '@ant-design/icons';
import { read, utils, writeFile } from 'xlsx';
import dayjs from 'dayjs';
import {
  CLIENT_IMPORT_EXPORT_HEADERS,
  type ClientImportDraftRow,
  parseClientImportMatrix,
  validateClientImportDrafts,
  clientIssuesByRowIndex,
  clientImportHasBlockingIssues,
  buildClientImportPersistRow,
} from '../utils/clientImportExport';
import {
  useClientsForBusinessId,
  useSuperAdminBulkImportClients,
} from '../../../hooks/useClients';
import { useBusiness } from '../../../hooks/useBusinesses';
import { getClientTypes } from '../../../types/business';
import { normalizeClientPhoneForDedup } from '../../../utils/clientMapper';
import type { ClientBulkImportProgressEvent } from '../../../utils/clientBulkImport';

const { Paragraph, Text } = Typography;

type Props = {
  businessId: string;
  businessName: string;
  canUse: boolean;
};

const ClientsImportExportTabContent: React.FC<Props> = ({
  businessId,
  businessName,
  canUse,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [draftRows, setDraftRows] = useState<ClientImportDraftRow[]>([]);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importProgress, setImportProgress] = useState<{
    total: number;
    processed: number;
    label: string;
    errors: { rowIndex: number; name: string; message: string }[];
    phase: 'running' | 'done';
    created: number;
    fatalMessage?: string;
  } | null>(null);

  const { data: business } = useBusiness(businessId);
  const allowedClientTypes = useMemo(
    () => getClientTypes(business ?? null),
    [business]
  );

  const { data: existingClients = [], isLoading } = useClientsForBusinessId(businessId, {
    enabled: canUse,
  });

  const importMutation = useSuperAdminBulkImportClients();

  const existingPhonesNormalized = useMemo(() => {
    const set = new Set<string>();
    for (const client of existingClients) {
      const phone = client.phone?.trim();
      if (!phone) continue;
      const normalized = normalizeClientPhoneForDedup(phone);
      if (normalized) set.add(normalized);
    }
    return set;
  }, [existingClients]);

  const validationIssues = useMemo(
    () =>
      validateClientImportDrafts(draftRows, allowedClientTypes, existingPhonesNormalized),
    [draftRows, allowedClientTypes, existingPhonesNormalized]
  );
  const issueMap = useMemo(() => clientIssuesByRowIndex(validationIssues), [validationIssues]);
  const blocked = clientImportHasBlockingIssues(validationIssues) || draftRows.length === 0;

  const handleDownloadTemplate = () => {
    const ws = utils.aoa_to_sheet([Array.from(CLIENT_IMPORT_EXPORT_HEADERS)]);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Clients');
    writeFile(wb, `modele-clients-${businessName.replace(/\s+/g, '_')}.xlsx`);
  };

  const handleExport = () => {
    if (!existingClients.length) {
      message.warning('Aucun client à exporter.');
      return;
    }
    const rows: (string | number)[][] = [Array.from(CLIENT_IMPORT_EXPORT_HEADERS)];
    for (const c of existingClients) {
      rows.push([
        c.name,
        c.phone || '',
        c.email || '',
        c.city || '',
        c.district || '',
        c.address || '',
        c.clientType || '',
        c.birthDate ? dayjs(c.birthDate).format('DD/MM/YYYY') : '',
        c.preferences?.marketingOptIn === true
          ? 'oui'
          : c.preferences?.marketingOptIn === false
            ? 'non'
            : '',
      ]);
    }
    const ws = utils.aoa_to_sheet(rows);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Clients');
    writeFile(wb, `export-clients-${businessName.replace(/\s+/g, '_')}.xlsx`);
    message.success('Export généré.');
  };

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = read(buf, { type: 'array' });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const matrix = utils.sheet_to_json<unknown[]>(sheet, { header: 1, defval: '' }) as unknown[][];
      const parsed = parseClientImportMatrix(matrix);
      if (!parsed.length) {
        message.error(
          'Impossible de lire le fichier. Vérifiez que la colonne « Nom » est présente dans l’en-tête.'
        );
        return;
      }
      setDraftRows(parsed);
      message.success(`${parsed.length} ligne(s) chargée(s).`);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur de lecture du fichier';
      message.error(msg);
    }
  };

  const applyProgress = useCallback((e: ClientBulkImportProgressEvent) => {
    setImportProgress((prev) => {
      if (!prev) return prev;
      switch (e.kind) {
        case 'start':
          return { ...prev, total: e.total, processed: 0, phase: 'running' };
        case 'row':
          return { ...prev, processed: e.index + 1, label: e.name };
        case 'success':
          return { ...prev, processed: e.index + 1, created: prev.created + 1, label: e.name };
        case 'fail':
          return {
            ...prev,
            processed: e.index + 1,
            label: e.name,
            errors: [...prev.errors, { rowIndex: e.index, name: e.name, message: e.message }],
          };
        case 'done':
          return {
            ...prev,
            phase: 'done',
            created: e.created,
            errors: e.errors,
            processed: prev.total,
          };
        default:
          return prev;
      }
    });
  }, []);

  const submitImport = async () => {
    if (blocked || importMutation.isPending) return;

    const rows = draftRows.map((draft) => buildClientImportPersistRow(draft, allowedClientTypes));

    setImportProgress({
      total: rows.length,
      processed: 0,
      label: '',
      errors: [],
      phase: 'running',
      created: 0,
    });
    setImportModalOpen(true);

    try {
      const res = await importMutation.mutateAsync({
        businessId,
        rows,
        onProgress: applyProgress,
      });
      if (res.created > 0) {
        message.success(`${res.created} client(s) créé(s).`);
        setDraftRows([]);
      }
      if (res.errors.length > 0) {
        message.warning(`${res.errors.length} ligne(s) en erreur.`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Erreur lors de l’import';
      setImportProgress((prev) =>
        prev ? { ...prev, phase: 'done', fatalMessage: msg } : prev
      );
      message.error(msg);
    }
  };

  if (!canUse) {
    return (
      <Empty description="Accès réservé au Super Admin ou au CSM lié à ce business." />
    );
  }

  return (
    <div>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <TeamOutlined />
          <Text strong>Import / export clients (Excel)</Text>
        </Space>
        <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0 }}>
          Le nom est obligatoire. Les doublons de téléphone (dans le fichier ou en base) bloquent
          l’import de la ligne concernée. Pas de suppression en masse — création uniquement.
        </Paragraph>
      </Card>

      <Space wrap style={{ marginBottom: 16 }}>
        <Button icon={<DownloadOutlined />} onClick={handleDownloadTemplate}>
          Télécharger le modèle
        </Button>
        <Button onClick={handleExport} disabled={!existingClients.length}>
          Exporter les clients existants
        </Button>
        <Button icon={<UploadOutlined />} onClick={() => fileInputRef.current?.click()}>
          Charger un fichier Excel
        </Button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,.xls,.csv"
          style={{ display: 'none' }}
          onChange={onPickFile}
        />
      </Space>

      {allowedClientTypes.length > 0 && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={`Types de clients autorisés : ${allowedClientTypes.join(', ')}`}
        />
      )}

      {validationIssues.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={`${validationIssues.length} ligne(s) à corriger avant import.`}
        />
      )}

      {isLoading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>Chargement…</div>
      ) : draftRows.length === 0 ? (
        <Empty description="Aucune donnée importée. Téléchargez le modèle ou chargez un fichier Excel." />
      ) : (
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          {draftRows.map((record) => {
            const msgs = issueMap.get(record.rowIndex);
            const hasError = Boolean(msgs?.length);
            return (
              <Card
                key={record.rowIndex}
                size="small"
                styles={{ body: { padding: 12 } }}
                style={{ borderLeft: hasError ? '3px solid #ff4d4f' : undefined }}
              >
                <Space direction="vertical" size={4} style={{ width: '100%' }}>
                  <Space wrap>
                    <Tag>Ligne {record.rowIndex}</Tag>
                    <Text strong type={hasError ? 'danger' : undefined}>
                      {record.name || '—'}
                    </Text>
                  </Space>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {[record.phone, record.city, record.district, record.clientType]
                      .filter(Boolean)
                      .join(' · ') || '—'}
                  </Text>
                  {hasError && (
                    <Text type="danger" style={{ fontSize: 12 }}>
                      {msgs!.join(' ')}
                    </Text>
                  )}
                </Space>
              </Card>
            );
          })}
        </Space>
      )}

      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <Button onClick={() => setDraftRows([])} disabled={!draftRows.length || importMutation.isPending}>
          Vider la liste
        </Button>
        <Button
          type="primary"
          loading={importMutation.isPending}
          disabled={blocked || importMutation.isPending}
          onClick={submitImport}
        >
          Créer les clients
        </Button>
      </div>

      <Modal
        title="Import clients en cours"
        open={importModalOpen}
        footer={
          importProgress?.phase === 'done' ? (
            <Button type="primary" onClick={() => setImportModalOpen(false)}>
              Fermer
            </Button>
          ) : null
        }
        closable={importProgress?.phase === 'done'}
        onCancel={() => importProgress?.phase === 'done' && setImportModalOpen(false)}
      >
        {importProgress && (
          <>
            {importProgress.fatalMessage && (
              <Alert type="error" message={importProgress.fatalMessage} style={{ marginBottom: 12 }} />
            )}
            <Progress
              percent={
                importProgress.total
                  ? Math.round((importProgress.processed / importProgress.total) * 100)
                  : 0
              }
              status={importProgress.phase === 'done' ? 'success' : 'active'}
            />
            <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
              {importProgress.phase === 'done'
                ? `${importProgress.created} client(s) créé(s)${
                    importProgress.errors.length
                      ? `, ${importProgress.errors.length} erreur(s)`
                      : ''
                  }`
                : importProgress.label || 'Préparation…'}
            </Text>
            {importProgress.errors.length > 0 && (
              <>
                <Divider />
                {importProgress.errors.slice(0, 8).map((err) => (
                  <div key={`${err.rowIndex}-${err.name}`} style={{ fontSize: 12, marginBottom: 4 }}>
                    <Text type="danger">
                      {err.name} : {err.message}
                    </Text>
                  </div>
                ))}
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default ClientsImportExportTabContent;
